import { useNavigate } from "react-router-dom";
import { ArrowRight, Mail, FileText, CreditCard, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { tenantBranding } from "@/hooks/useTenant";

/**
 * Patient-facing tenant landing.
 *
 * IMPORTANT: this page is only rendered when a tenant has been resolved
 * (subdomain or `?tenant=` param). The corporate root (`thaleshealth.com`)
 * renders `ThalesLandingB2B` instead. See `App.js` for the routing switch.
 *
 * All hard-coded Fleni references have been replaced with values coming from
 * the tenant's `branding` config so the same page can serve any institution.
 */
const Landing = ({ tenant }) => {
  const navigate = useNavigate();
  const brand = tenantBranding(tenant);

  // Some tenants (e.g. Consultorio Privado / Thales white-label) don't have a
  // logo image and want a text wordmark instead. Falling back to the tenant
  // name keeps the header from ever being empty.
  const renderLogo = (className) =>
    brand.logoUrl ? (
      <img
        src={brand.logoUrl}
        alt={brand.name}
        className={className}
        data-testid="tenant-landing-logo"
      />
    ) : (
      <span
        className={`${className} font-semibold tracking-tight text-[${brand.primaryColor}]`}
        style={{ color: brand.primaryColor, fontFamily: "'Manrope', sans-serif" }}
        data-testid="tenant-landing-logo-text"
      >
        {brand.name}
      </span>
    );

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Navigation - Logo only */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-16 sm:h-20">
            {renderLogo("h-9 sm:h-11 object-contain")}
          </div>
        </div>
      </nav>

      {/* Hero Section - Centered, minimal */}
      <section className="min-h-screen flex items-center justify-center pt-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <p
            className="text-xs font-bold tracking-[0.2em] uppercase mb-8"
            style={{ fontFamily: "'Manrope', sans-serif", color: brand.accentColor }}
          >
            Consultas Medicas Asincronicas
          </p>
          <p className="text-lg sm:text-xl text-slate-500 leading-relaxed mb-12 max-w-2xl mx-auto">
            Resuelve dudas medicas no urgentes con tu profesional de cabecera de forma rapida, segura y accesible.
          </p>
          <div className="flex flex-col gap-3 max-w-md mx-auto sm:max-w-2xl">
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                onClick={() => navigate("/mis-medicos")}
                className="text-white h-14 sm:h-14 px-6 sm:px-8 rounded-full text-base font-medium tracking-wide w-full sm:w-auto sm:flex-1 sm:max-w-xs"
                style={{ backgroundColor: brand.accentColor }}
                data-testid="hero-get-started-btn"
              >
                Ya consulté antes
                <ArrowRight className="ml-3 h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate("/primera-vez")}
                className="h-14 sm:h-14 px-6 sm:px-8 rounded-full text-base font-medium tracking-wide w-full sm:w-auto sm:flex-1 sm:max-w-xs"
                style={{ borderColor: brand.primaryColor, color: brand.primaryColor }}
                data-testid="hero-first-time-btn"
              >
                Es mi primera vez
              </Button>
            </div>
            <button
              type="button"
              onClick={() => navigate("/auth")}
              className="text-sm text-slate-500 hover:text-slate-800 mt-2 transition-colors"
              data-testid="hero-doctor-access-btn"
            >
              ¿Sos profesional médico? Accedé acá →
            </button>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-16 lg:mb-20">
            <p
              className="text-xs font-bold tracking-[0.2em] uppercase mb-4"
              style={{ fontFamily: "'Manrope', sans-serif", color: brand.accentColor }}
            >
              Proceso simple
            </p>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-light tracking-tight"
              style={{ fontFamily: "'Manrope', sans-serif", color: brand.primaryColor }}
            >
              Como funciona
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-0">
            {[
              {
                icon: Mail,
                step: "01",
                title: "Inicia tu consulta",
                description:
                  "Hace clic en 'Iniciar Consulta' y completa tus datos personales.",
              },
              {
                icon: FileText,
                step: "02",
                title: "Describe tu necesidad",
                description:
                  "Selecciona el tipo de consulta y describe detalladamente tu situacion.",
              },
              {
                icon: CreditCard,
                step: "03",
                title: "Confirmacion",
                description: `Confirma tu consulta. Un profesional medico ${
                  tenant ? `de ${brand.name}` : ""
                } sera asignado.`.trim(),
              },
              {
                icon: CheckCircle,
                step: "04",
                title: "Recibi tu respuesta",
                description:
                  "El profesional responde dentro del plazo y recibis un PDF con el informe oficial por email.",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className={`p-8 border border-slate-200 hover:border-slate-400 transition-all duration-300 group ${idx === 0 ? '' : 'md:border-l-0'} ${idx >= 2 ? 'md:border-t-0 lg:border-t' : ''} ${idx >= 1 ? 'lg:border-l-0' : ''}`}
                data-testid={`step-${item.step}`}
              >
                <span
                  className="text-5xl font-extralight block mb-6 transition-colors"
                  style={{ fontFamily: "'Manrope', sans-serif", color: `${brand.accentColor}33` }}
                >
                  {item.step}
                </span>
                <div
                  className="h-10 w-10 border flex items-center justify-center mb-5 transition-colors"
                  style={{ borderColor: `${brand.primaryColor}33` }}
                >
                  <item.icon className="h-5 w-5" style={{ color: brand.primaryColor }} />
                </div>
                <h3
                  className="text-lg font-medium text-slate-900 tracking-tight mb-3"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {item.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="border-t-4 py-12 sm:py-16"
        style={{ backgroundColor: brand.primaryColor, borderTopColor: brand.accentColor }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              {brand.logoUrl ? (
                <img
                  src={brand.logoUrl}
                  alt={brand.name}
                  className="h-10 sm:h-12 object-contain brightness-0 invert mb-3"
                />
              ) : (
                <span
                  className="text-2xl font-semibold text-white mb-3 block"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  {brand.name}
                </span>
              )}
              {brand.slug === "fleni" && (
                <>
                  <p className="text-sm text-slate-300">
                    Neurologia · Neurocirugia · Rehabilitacion
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Montaneses 2325, Buenos Aires, Argentina
                  </p>
                </>
              )}
            </div>
            <div className="text-sm text-slate-300 text-left md:text-right">
              <p>&copy; {new Date().getFullYear()} {brand.name}. Todos los derechos reservados.</p>
              <p className="text-xs text-slate-400 mt-1">
                Plataforma de consultas medicas asincronicas
              </p>
              <p className="text-[11px] text-slate-400 mt-3">
                Powered by <span className="font-medium">Thales Health</span>
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
